import React, { useEffect, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";
import { useAuth } from "../../context/AuthContext";
import type { SparePart } from "../../types";

export default function SparePartsInventory() {
  const { hasPermission } = useAuth();
  const [parts, setParts] = useState<SparePart[]>([]);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPart, setActionPart] = useState<SparePart | null>(null);
  const [actionType, setActionType] = useState<"receive" | "issue" | "adjust" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = lowStockOnly ? "?lowStock=true" : "";
      setParts(await api.get<SparePart[]>(`/api/spare-parts${q}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load spare parts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowStockOnly]);

  const columns: Column<SparePart>[] = [
    { header: "Part #", accessor: (p) => <span className="font-mono text-xs">{p.part_number}</span> },
    { header: "Name", accessor: (p) => p.part_name },
    { header: "Category", accessor: (p) => p.category ?? "-" },
    {
      header: "Stock",
      accessor: (p) => (
        <span className={p.current_stock <= p.min_stock ? "font-semibold text-red-600" : ""}>
          {p.current_stock} {p.uom}
        </span>
      ),
    },
    { header: "Min / Max", accessor: (p) => `${p.min_stock} / ${p.max_stock}` },
    { header: "Location", accessor: (p) => p.storage_location ?? "-" },
    {
      header: "Actions",
      accessor: (p) => (
        <div className="flex gap-1">
          {hasPermission("*", "inventory:receive") && (
            <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => { setActionPart(p); setActionType("receive"); }}>Receive</button>
          )}
          {hasPermission("*", "inventory:issue") && (
            <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => { setActionPart(p); setActionType("issue"); }}>Issue</button>
          )}
          {hasPermission("*", "inventory:adjust") && (
            <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => { setActionPart(p); setActionType("adjust"); }}>Adjust</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Spare Parts Inventory</h1>
        <p className="text-sm text-slate-500">Goods issue, goods receive, cycle count, and reorder alerts.</p>
      </div>
      <Card>
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
          Show low-stock items only
        </label>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : <DataTable columns={columns} rows={parts} keyField={(p) => p.id} />}
      </Card>

      <Modal open={!!actionPart} onClose={() => setActionPart(null)} title={`${actionType?.toUpperCase()} — ${actionPart?.part_name ?? ""}`}>
        {actionPart && actionType && (
          <StockActionForm
            part={actionPart}
            actionType={actionType}
            onDone={() => {
              setActionPart(null);
              load();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function StockActionForm({
  part,
  actionType,
  onDone,
}: {
  part: SparePart;
  actionType: "receive" | "issue" | "adjust";
  onDone: () => void;
}) {
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (actionType === "adjust") {
        await api.post(`/api/spare-parts/${part.id}/adjust`, { newQuantity: Number(quantity) });
      } else {
        await api.post(`/api/spare-parts/${part.id}/${actionType}`, { quantity: Number(quantity) });
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <p className="text-sm text-slate-500">
        Current stock: <Badge value="RUNNING" label={`${part.current_stock} ${part.uom}`} />
      </p>
      <input
        className="input-field"
        type="number"
        min={0}
        required
        placeholder={actionType === "adjust" ? "New quantity (physical count)" : "Quantity"}
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
      />
      <button type="submit" disabled={submitting} className="btn-primary w-full capitalize">
        {submitting ? "Saving…" : actionType}
      </button>
    </form>
  );
}
